<template>
  <Teleport to="body">
    <Transition name="modal">
      <div class="attendance-overlay" v-if="visible" @click.self="handleClose">
        <div class="attendance-popup" :class="{ 'reward-mode': step === 'reward', 'milestone-achieved': milestoneAchieved }">
          
          <!-- ========== STEP 1: 출근/퇴근 확인 ========== -->
          <Transition name="slide-fade" mode="out-in">
            <div v-if="step === 'confirm'" key="confirm" class="step-content">
              <div class="popup-icon">
                {{ toastType === 'checkIn' ? '💼' : '🌙' }}
              </div>
              <div class="popup-content">
                <h3 class="title">{{ toastType === 'checkIn' ? '출근 도장' : '퇴근 도장' }}</h3>
                <p class="message">{{ toastType === 'checkIn' ? '오늘 하루도 화이팅!' : '오늘 하루 수고했어요!' }}</p>
                <!-- ✅ 수정: 출근 시간 기준 경과 시간 -->
                <p class="sub-message" v-if="toastType === 'checkOut' && elapsedFromCheckIn">
                  퇴근시간이 <strong>{{ elapsedFromCheckIn }}</strong> 지났습니다!
                </p>
              </div>
              <div class="popup-buttons">
                <button class="confirm" :class="{ 'loading': isLoading }" :disabled="isLoading" @click="handleConfirm">
                  <span v-if="isLoading">처리 중...</span>
                  <span v-else>{{ toastType === 'checkIn' ? '✓ 출근하기' : '✓ 퇴근하기' }}</span>
                </button>
                <button class="cancel" :disabled="isLoading" @click="handleClose">나중에 할게요</button>
              </div>
            </div>

            <!-- ========== STEP 2: 보상 화면 ========== -->
            <div v-else-if="step === 'reward'" key="reward" class="step-content reward-content">
              
              <!-- 월간 출석 헤더 -->
              <div class="month-header">
                <span class="month-title">📅 {{ currentMonth }}월 출석 현황</span>
              </div>

              <!-- 월간 캘린더 -->
              <div class="calendar-section">
                <div class="calendar-weekdays">
                  <span v-for="day in ['일', '월', '화', '수', '목', '금', '토']" :key="day">{{ day }}</span>
                </div>
                <div class="calendar-grid">
                  <div 
                    v-for="(day, index) in monthDays" 
                    :key="index"
                    class="calendar-day"
                    :class="{ 
                      'empty': !day.date,
                      'attended': day.attended,
                      'today': day.isToday,
                      'just-stamped': day.justStamped,
                      'future': day.isFuture,
                      'missed': day.missed
                    }"
                  >
                    <span class="day-number" v-if="day.date">{{ day.date }}</span>
                    <span class="day-dot" v-if="day.attended">●</span>
                  </div>
                </div>
              </div>

              <!-- 출석률 프로그레스 -->
              <div class="attendance-progress">
                <div class="progress-header">
                  <span class="progress-label">
                    출석 <strong>{{ attendedDays }}/{{ totalWorkdays }}일</strong> ({{ attendanceRate }}%)
                  </span>
                </div>
                <div class="progress-bar-wrapper">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill" 
                      :class="{ 
                        'level-80': attendanceRate >= 80,
                        'level-100': attendanceRate >= 100,
                        'level-110': attendanceRate >= 110,
                        'level-120': attendanceRate >= 120
                      }"
                      :style="{ width: `${Math.min(attendanceRate / 1.2, 100)}%` }"
                    ></div>
                  </div>
                  <!-- 80% 마커 (66.67% 위치) -->
                  <div class="progress-pin" :class="{ 'achieved': attendanceRate >= 80 }" style="left: 66.67%">
                    <div class="pin-icon">🎟️</div>
                    <div class="pin-label">80%</div>
                  </div>
                  <!-- 100% 마커 (83.33% 위치) -->
                  <div class="progress-pin" :class="{ 'achieved': attendanceRate >= 100 }" style="left: 83.33%">
                    <div class="pin-icon">☕</div>
                    <div class="pin-label">100%</div>
                  </div>
                  <!-- 120% 마커 (100% 위치) -->
                  <div class="progress-pin" :class="{ 'achieved': attendanceRate >= 120 }" style="left: 100%">
                    <div class="pin-icon">🎁</div>
                    <div class="pin-label">120%</div>
                  </div>
                </div>
              </div>

              <!-- 월간 보상 -->
              <div class="reward-badges">
                <!-- 80% 룰렛 티켓 -->
                <div class="reward-badge ticket" :class="{ 'achieved': attendanceRate >= 80 }">
                  <span class="badge-icon">🎟️</span>
                  <span class="badge-text">룰렛 2장</span>
                  <span class="badge-status">{{ attendanceRate >= 80 ? '달성!' : `${attendanceRate}%` }}</span>
                </div>
                <!-- 100% 선물 -->
                <div class="reward-badge gift" :class="{ 'achieved': attendanceRate >= 100 }">
                  <span class="badge-icon">☕</span>
                  <span class="badge-text">커피</span>
                  <span class="badge-status">{{ attendanceRate >= 100 ? '달성!' : `${attendanceRate}%` }}</span>
                </div>
                <!-- 110% 초과근무 -->
                <div class="reward-badge overtime" :class="{ 'achieved': attendanceRate >= 110 }">
                  <span class="badge-icon">🍰</span>
                  <span class="badge-text">디저트</span>
                  <span class="badge-status">{{ attendanceRate >= 110 ? '달성!' : `${attendanceRate}%` }}</span>
                </div>
                <!-- 120% 초과근무 -->
                <div class="reward-badge overtime" :class="{ 'achieved': attendanceRate >= 120 }">
                  <span class="badge-icon">🎁</span>
                  <span class="badge-text">스페셜</span>
                  <span class="badge-status">{{ attendanceRate >= 120 ? '달성!' : `${attendanceRate}%` }}</span>
                </div>
              </div>

              <!-- 룰렛 티켓 지급 알림 -->
              <Transition name="slide-fade">
                <div class="ticket-reward-banner" v-if="ticketRewarded">
                  <span class="ticket-icon">🎟️</span>
                  <span class="ticket-text">룰렛 티켓 <strong>2장</strong> 지급!</span>
                </div>
              </Transition>

              <!-- 오늘의 메시지 -->
              <div class="today-message">
                <p class="message-text">{{ todayMessage }}</p>
              </div>

              <!-- 닫기 버튼 -->
              <button class="close-btn" @click="handleClose">
                확인했어요!
              </button>

              <!-- Confetti 효과 -->
              <div class="confetti-container" v-if="showConfetti">
                <div 
                  v-for="n in 50" 
                  :key="n" 
                  class="confetti"
                  :style="getConfettiStyle(n)"
                ></div>
              </div>

              <!-- 마일스톤 달성 효과 -->
              <div class="milestone-burst" v-if="milestoneAchieved">
                <div class="burst-ring"></div>
                <div class="burst-ring delay-1"></div>
                <div class="burst-ring delay-2"></div>
              </div>
            </div>
          </Transition>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, inject, onMounted, onUnmounted, computed } from 'vue';

export default {
  setup() {
    const visible = ref(false);
    const toastType = ref('checkIn');
    const step = ref('confirm'); // 'confirm' | 'reward'
    
    const member_info = inject('member_info');
    const attened_getToday = inject('attened_getToday');
    const attend_checkIn = inject('attend_checkIn');
    const attend_checkOut = inject('attend_checkOut');
    const getMonthAttendance = inject('getMonthAttendance', () => async () => []);
    const sql_query = inject('sql_query');
    
    let periodicCheckInterval = null;
    let timeInterval = null;
    const currentTime = ref(new Date());

    // 보상 화면 상태
    const monthAttendance = ref([]);
    const justStamped = ref(false);
    const showConfetti = ref(false);
    const milestoneAchieved = ref(false);
    const ticketRewarded = ref(false);
    
    // 출근 완료 후 재팝업 방지용
    const lastActionTime = ref(null);
    
    // 출근 시간 저장 (경과 시간 계산용)
    const checkInTime = ref(null);

    // ========== localStorage 기반 출석 캐시 ==========
    const STORAGE_KEY = 'attendance_today';

    function getTodayStr() {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    }

    function saveAttendanceCache(data) {
      try {
        const cache = {
          date: getTodayStr(),
          mb_id: member_info.mb_id,
          check_in: data?.check_in || null,
          check_out: data?.check_out || null,
          timestamp: Date.now()
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
      } catch (e) { /* localStorage 접근 불가 시 무시 */ }
    }

    function getAttendanceCache() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const cache = JSON.parse(raw);
        // 오늘 날짜 + 같은 유저만 유효
        if (cache.date !== getTodayStr() || cache.mb_id !== member_info.mb_id) {
          localStorage.removeItem(STORAGE_KEY);
          return null;
        }
        return cache;
      } catch (e) { return null; }
    }

    // 현재 월
    const currentMonth = computed(() => {
      const now = new Date();
      return now.getMonth() + 1;
    });

    // 출근으로부터 경과 시간 계산
    const elapsedFromCheckIn = computed(() => {
      if (!checkInTime.value) return '';
      
      const now = currentTime.value;
      const checkIn = new Date(checkInTime.value);
      const diff = now - checkIn;
      
      if (diff < 0) return '';
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0 && minutes > 0) {
        return `${hours}시간 ${minutes}분`;
      } else if (hours > 0) {
        return `${hours}시간`;
      } else {
        return `${minutes}분`;
      }
    });

    // 이번 달 캘린더 데이터 생성 (단순화)
    const monthDays = computed(() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const today = now.getDate();
      
      const firstDay = new Date(year, month, 1).getDay();
      const lastDate = new Date(year, month + 1, 0).getDate();
      
      const days = [];
      
      for (let i = 0; i < firstDay; i++) {
        days.push({ date: null, empty: true });
      }
      
      for (let d = 1; d <= lastDate; d++) {
        // 오늘 도장 찍었으면 표시 (출근일 때만)
        const isStampedToday = d === today && step.value === 'reward' && toastType.value === 'checkIn';
        const attended = monthAttendance.value.includes(d) || isStampedToday;
        
        const isToday = d === today;
        const isFuture = d > today;
        const isWeekend = new Date(year, month, d).getDay() === 0 || new Date(year, month, d).getDay() === 6;
        const missed = d < today && !attended && !isWeekend;
        
        days.push({
          date: d,
          attended,
          isToday,
          isFuture,
          missed,
          justStamped: justStamped.value && d === today && toastType.value === 'checkIn'
        });
      }
      
      return days;
    });

    // 출석 통계 - 출근일 때만 +1
    const attendedDays = computed(() => {
      const now = new Date();
      const today = now.getDate();
      
      const todayAttended = (step.value === 'reward' && toastType.value === 'checkIn') ? 1 : 0;
      
      return monthAttendance.value.filter(d => d <= today).length + todayAttended;
    });

    // 이번 달 전체 평일 수
    const totalWorkdays = computed(() => {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      const lastDate = new Date(year, month + 1, 0).getDate();
      
      let count = 0;
      for (let d = 1; d <= lastDate; d++) {
        const dayOfWeek = new Date(year, month, d).getDay();
        if (dayOfWeek !== 0 && dayOfWeek !== 6) {
          count++;
        }
      }
      return count;
    });

    // 출석률
    const attendanceRate = computed(() => {
      if (totalWorkdays.value === 0) return 0;
      return Math.round((attendedDays.value / totalWorkdays.value) * 100);
    });

    const todayMessages = [
      '오늘도 멋지게 출석했어요! 💪',
      '꾸준함이 최고의 무기예요! 🎯',
      '오늘 하루도 화이팅! 🌟',
      '작은 습관이 큰 변화를 만들어요! 🚀',
      '당신의 노력이 빛나고 있어요! ✨',
      '매일 성장하는 당신, 멋져요! 🌱',
      '오늘도 한 걸음 더! 👣',
    ];

    const todayMessage = computed(() => {
      if (toastType.value === 'checkOut') {
        return '🌙 오늘 하루도 수고했어요! 푹 쉬세요~';
      }
      
      if (attendanceRate.value >= 120) {
        return '👑 120% 달성! 당신은 전설의 근무왕!';
      }
      if (attendanceRate.value >= 110) {
        return '🔥 110% 초과 근무! 대단해요!';
      }
      if (attendanceRate.value >= 100) {
        return '🎉 100% 완벽 출석! 커피 받아가세요!';
      }
      if (attendanceRate.value >= 80) {
        return '🎟️ 80% 달성! 룰렛 티켓 2장을 받았어요!';
      }
      const idx = new Date().getDate() % todayMessages.length;
      return todayMessages[idx];
    });

    // 퇴근 가능: 18:30 이후
    function isCheckOutTime() {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      return hours > 18 || (hours === 18 && minutes >= 30);
    }

    // 테스트 계정
    const TEST_ACCOUNTS = ['type only using tester mb_id here. when if you need'];
    const isTestAccount = () => TEST_ACCOUNTS.includes(member_info.mb_id);

    // 통합 체크 함수 (localStorage 캐시 기반 오프라인 방어)
    async function periodicCheck() {
      if (visible.value) return;
      
      // 쿨다운 체크
      if (lastActionTime.value && Date.now() - lastActionTime.value < 60 * 1000) {
        return;
      }
      
      // 네트워크 연결 상태 체크
      if (!navigator.onLine) {
        console.log('⏸️ 오프라인 상태 - 출석 체크 스킵');
        return;
      }
      
      let todayAttend;
      try {
        todayAttend = await attened_getToday(member_info.mb_id);
      } catch (error) {
        // 네트워크 오류 → localStorage 캐시로 판단
        console.warn('⚠️ 출석 정보 조회 실패 - 캐시 확인:', error.message);
        const cache = getAttendanceCache();
        if (cache?.check_in) {
          // 캐시에 출근 기록 있음 → 팝업 안 띄움
          console.log('✅ 캐시에 출근 기록 있음 - 팝업 스킵');
          return;
        }
        // 캐시도 없으면 → 다음 체크에서 재시도 (팝업 안 띄움)
        console.log('⏸️ 캐시 없음 - 다음 체크에서 재시도');
        return;
      }
      
      // 응답이 유효하지 않은 경우 → 캐시 확인 후 스킵
      if (todayAttend === null || todayAttend === undefined) {
        console.warn('⚠️ 출석 정보 응답 없음 - 캐시 확인');
        const cache = getAttendanceCache();
        if (cache?.check_in) return;
        return;
      }
      
      // DB 조회 성공 → 캐시 업데이트
      if (todayAttend?.[0]) {
        saveAttendanceCache(todayAttend[0]);
      }
      
      // 테스트 계정
      if (isTestAccount()) {
        toastType.value = 'checkIn';
        step.value = 'confirm';
        visible.value = true;
        return;
      }
      
      // 1. 출근 기록 없음 → 출근 팝업 (하루 종일)
      if (!todayAttend?.[0]?.check_in) {
        // 이중 안전장치: 캐시에 출근 기록 있으면 스킵
        const cache = getAttendanceCache();
        if (cache?.check_in) {
          console.log('⚠️ DB에 없지만 캐시에 출근 기록 있음 - 팝업 스킵 (DB 동기화 대기)');
          return;
        }
        toastType.value = 'checkIn';
        step.value = 'confirm';
        visible.value = true;
        return;
      }
      
      // 2. 출근 했고 + 퇴근 안 했고 + 18:30 이후 → 퇴근 팝업
      if (todayAttend?.[0]?.check_in && !todayAttend?.[0]?.check_out && isCheckOutTime()) {
        toastType.value = 'checkOut';
        step.value = 'confirm';
        checkInTime.value = todayAttend[0].check_in;
        
        currentTime.value = new Date();
        visible.value = true;
        
        if (!timeInterval) {
          timeInterval = setInterval(() => {
            currentTime.value = new Date();
          }, 60 * 1000);
        }
      }
    }

    // Confetti 스타일
    const getConfettiStyle = (n) => {
      const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8'];
      const randomColor = colors[n % colors.length];
      const randomX = Math.random() * 100;
      const randomDelay = Math.random() * 0.5;
      const randomDuration = 1 + Math.random() * 2;
      
      return {
        '--x': `${randomX}%`,
        '--color': randomColor,
        '--delay': `${randomDelay}s`,
        '--duration': `${randomDuration}s`,
        left: `${randomX}%`
      };
    };

    // 마일스톤 달성 체크
    const milestoneLevels = [80, 100, 110, 120];
    const checkMilestone = () => {
      return milestoneLevels.includes(attendanceRate.value);
    };

    onMounted(async () => {
      setTimeout(() => {
        periodicCheck();
      }, 500);
      
      periodicCheckInterval = setInterval(() => {
        periodicCheck();
      }, 10 * 60 * 1000);
      
      // 절전 복귀 시: 화면 활성화될 때 체크 (debounce)
      let visibilityTimeout = null;
      window.__attendanceVisibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          // 절전 복귀 후 3초 대기 (네트워크 안정화)
          clearTimeout(visibilityTimeout);
          visibilityTimeout = setTimeout(() => {
            periodicCheck();
          }, 3000);
        }
      };
      document.addEventListener('visibilitychange', window.__attendanceVisibilityHandler);
      
      // 오프라인→온라인 전환 시 체크 (2초 대기)
      window.__attendanceOnlineHandler = () => {
        setTimeout(() => {
          periodicCheck();
        }, 2000);
      };
      window.addEventListener('online', window.__attendanceOnlineHandler);
      
      window.addEventListener('attendance-updated', handleAttendanceUpdated);
    });

    onUnmounted(() => {
      if (periodicCheckInterval) clearInterval(periodicCheckInterval);
      if (timeInterval) clearInterval(timeInterval);
      document.removeEventListener('visibilitychange', window.__attendanceVisibilityHandler);
      window.removeEventListener('online', window.__attendanceOnlineHandler);
      window.removeEventListener('attendance-updated', handleAttendanceUpdated);
    });

    async function handleAttendanceUpdated() {
      const todayAttend = await attened_getToday(member_info.mb_id);
      
      if (toastType.value === 'checkIn' && todayAttend?.[0]?.check_in && step.value === 'confirm') {
        visible.value = false;
      }
      
      if (toastType.value === 'checkOut' && todayAttend?.[0]?.check_out && step.value === 'confirm') {
        visible.value = false;
        if (timeInterval) {
          clearInterval(timeInterval);
          timeInterval = null;
        }
      }
    }

    const isLoading = ref(false);

    async function handleConfirm() {
      if (isLoading.value) return;
      isLoading.value = true;

      try {
        if (toastType.value === 'checkIn') {
          await attend_checkIn(member_info.mb_id);
          // 출근 성공 → localStorage 캐시 저장
          saveAttendanceCache({ check_in: new Date().toISOString(), check_out: null });
        } else {
          await attend_checkOut(member_info.mb_id);
          // 퇴근 성공 → localStorage 캐시 업데이트
          const cache = getAttendanceCache();
          saveAttendanceCache({ 
            check_in: cache?.check_in || new Date().toISOString(), 
            check_out: new Date().toISOString() 
          });
        }
        
        // 액션 시간 기록 (재팝업 방지)
        lastActionTime.value = Date.now();
        
        // 월간 출석 데이터 로드
        monthAttendance.value = await getMonthAttendance(member_info.mb_id);
        
        // 보상 화면으로 전환
        step.value = 'reward';
        
        // 이벤트 발생
        window.dispatchEvent(new CustomEvent('attendance-updated'));
        
        // 애니메이션 - 출근일 때만 confetti
        setTimeout(() => {
          justStamped.value = true;
          if (toastType.value === 'checkIn') {
            showConfetti.value = true;
          }
        }, 300);

        // 마일스톤 체크 (출근일 때만)
        if (toastType.value === 'checkIn' && checkMilestone()) {
          setTimeout(() => {
            milestoneAchieved.value = true;
          }, 800);
        }

        // 80% 달성 시 룰렛 티켓 2장 지급 (출근일 때만, 월별 중복 방지)
        if (toastType.value === 'checkIn' && attendanceRate.value >= 80) {
          try {
            const now = new Date();
            const source = `attendance_80_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            
            const existing = await sql_query(
              `SELECT idx FROM roulette_tickets WHERE mb_id = ? AND source = ? LIMIT 1`,
              [member_info.mb_id, source]
            );
            
            if (!existing?.length) {
              await sql_query(
                `INSERT INTO roulette_tickets (mb_id, ticket_count, used_count, source, status, expired_at, created_at)
                 VALUES (?, 2, 0, ?, 'active', DATE_ADD(LAST_DAY(NOW()), INTERVAL 1 DAY), NOW())`,
                [member_info.mb_id, source]
              );
              ticketRewarded.value = true;
              setTimeout(() => { ticketRewarded.value = false; }, 5000);
            }
          } catch (e) {
            console.warn('룰렛 티켓 지급 실패:', e.message);
          }
        }

        setTimeout(() => {
          showConfetti.value = false;
        }, 3000);
        
        if (timeInterval) {
          clearInterval(timeInterval);
          timeInterval = null;
        }
      } finally {
        isLoading.value = false;
      }
    }

    function handleClose() {
      visible.value = false;
      step.value = 'confirm';
      justStamped.value = false;
      showConfetti.value = false;
      milestoneAchieved.value = false;
      ticketRewarded.value = false;
      checkInTime.value = null;
      
      if (timeInterval) {
        clearInterval(timeInterval);
        timeInterval = null;
      }
    }

    return {
      visible,
      toastType,
      step,
      elapsedFromCheckIn,
      currentMonth,
      monthDays,
      attendedDays,
      totalWorkdays,
      attendanceRate,
      justStamped,
      showConfetti,
      milestoneAchieved,
      ticketRewarded,
      todayMessage,
      isLoading,
      getConfettiStyle,
      handleConfirm,
      handleClose,
      handleAttendanceUpdated
    };
  }
}
</script>

<style scoped>
.attendance-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-modal, 9999);
  padding: var(--font-size-lg);
}

.attendance-popup {
  background: var(--color-background);
  border-radius: var(--radius-xl, 20px);
  padding: var(--font-size-xl) var(--font-size-xl) var(--font-size-lg);
  box-shadow: var(--shadow-lg), 0 0 0 1px var(--color-surface-reverse);
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
  width: 100%;
  text-align: center;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.attendance-popup.reward-mode {
  max-width: 360px;
  padding: 24px 20px;
}

.attendance-popup.milestone-achieved {
  animation: milestoneShake 0.6s ease-in-out;
}

@keyframes milestoneShake {
  0%, 100% { transform: translateX(0); }
  10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
  20%, 40%, 60%, 80% { transform: translateX(4px); }
}

.step-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--font-size-md);
  width: 100%;
}

/* ========== STEP 1: 출근/퇴근 확인 스타일 ========== */
.popup-icon {
  font-size: 56px;
  line-height: 1;
  animation: bounce 1s ease-in-out infinite;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.popup-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.popup-content .title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  margin: 0;
}

.popup-content .message {
  font-size: var(--font-size-base);
  color: var(--color-text-secondary);
  margin: 0;
}

.popup-content .sub-message {
  font-size: var(--font-size-sm);
  color: var(--color-special-accent);
  margin: 0;
  padding: 6px 12px;
  background: var(--color-surface-reverse);
  border-radius: var(--radius-full, 999px);
}

.popup-content .sub-message strong {
  font-weight: var(--font-weight-bold);
}

.popup-buttons {
  display: flex;
  flex-direction: column;
  gap: var(--font-size-sm);
  width: 100%;
  margin-top: var(--font-size-sm);
}

.popup-buttons button {
  width: 100%;
  padding: var(--font-size-md);
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.popup-buttons .confirm {
  background: var(--color-brand);
  color: var(--color-text-black);
  font-size: var(--font-size-lg);
  padding: var(--font-size-lg);
}

.popup-buttons .confirm:hover:not(:disabled) {
  background: var(--color-special-accent);
  transform: scale(1.02);
}

.popup-buttons .confirm:active:not(:disabled) {
  transform: scale(0.98);
}

.popup-buttons .confirm:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.popup-buttons .confirm.loading {
  background: var(--color-surface);
  color: var(--color-text-secondary);
}

.popup-buttons .cancel {
  background: transparent;
  color: var(--color-text-tertiary);
  padding: var(--font-size-sm);
}

.popup-buttons .cancel:hover:not(:disabled) {
  color: var(--color-text-secondary);
}

.popup-buttons .cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ========== STEP 2: 보상 화면 스타일 ========== */
.reward-content {
  gap: 16px;
}

/* 월간 헤더 */
.month-header {
  width: 100%;
  text-align: center;
}

.month-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text-primary);
}

/* 캘린더 섹션 */
.calendar-section {
  width: 100%;
  padding: 12px;
  background: var(--color-surface);
  border-radius: 16px;
}

.calendar-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
  margin-bottom: 8px;
}

.calendar-weekdays span {
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--color-text-tertiary);
  padding: 4px 0;
}

.calendar-weekdays span:first-child {
  color: var(--color-error, #ff6b6b);
}

.calendar-weekdays span:last-child {
  color: var(--color-info, #4a9eff);
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.calendar-day {
  aspect-ratio: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  position: relative;
  transition: all 0.2s ease;
}

.calendar-day.empty {
  visibility: hidden;
}

.calendar-day .day-number {
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-secondary);
}

.calendar-day .day-dot {
  font-size: 6px;
  color: var(--color-brand);
  position: absolute;
  bottom: 2px;
}

/* 출석한 날: border만 */
.calendar-day.attended {
  border: 2px solid var(--color-brand);
}

.calendar-day.attended .day-number {
  color: var(--color-brand);
  font-weight: 700;
}

.calendar-day.attended .day-dot {
  display: none;
}

/* 오늘: fill */
.calendar-day.today {
  background: var(--color-brand);
}

.calendar-day.today .day-number {
  color: var(--color-text-black);
  font-weight: 700;
}

/* 오늘 + 출석: fill 우선 */
.calendar-day.today.attended {
  background: var(--color-brand);
  border: none;
}

.calendar-day.today.attended .day-number {
  color: var(--color-text-black);
}

.calendar-day.just-stamped {
  animation: stampSlam 0.5s cubic-bezier(0.36, 0.07, 0.19, 0.97);
}

@keyframes stampSlam {
  0% { 
    transform: scale(5); 
    opacity: 0;
  }
  40% { 
    transform: scale(0.6); 
    opacity: 1;
  }
  60% { 
    transform: scale(1.1); 
  }
  80% { 
    transform: scale(0.95); 
  }
  100% { 
    transform: scale(1); 
    opacity: 1;
  }
}

.calendar-day.future .day-number {
  color: var(--color-text-tertiary);
  opacity: 0.5;
}

.calendar-day.missed {
  background: var(--color-surface-reverse);
}

.calendar-day.missed .day-number {
  color: var(--color-text-tertiary);
  text-decoration: line-through;
  opacity: 0.5;
}

/* 출석률 프로그레스 */
.attendance-progress {
  width: 100%;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.progress-label {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.progress-label strong {
  color: var(--color-text-primary);
  font-weight: 700;
}

.progress-bar-wrapper {
  position: relative;
  padding-bottom: 28px;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: var(--color-surface);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: var(--color-text-tertiary);
  border-radius: 4px;
  transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.progress-fill.level-80 {
  background: var(--color-brand);
}

.progress-fill.level-100 {
  background: linear-gradient(90deg, var(--color-brand) 0%, #4ade80 100%);
}

.progress-fill.level-110 {
  background: linear-gradient(90deg, var(--color-brand) 0%, #4ade80 50%, #60a5fa 100%);
}

.progress-fill.level-120 {
  background: linear-gradient(90deg, var(--color-brand) 0%, #4ade80 33%, #60a5fa 66%, #a78bfa 100%);
}

.progress-pin {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.progress-pin.achieved {
  opacity: 1;
}

.progress-pin .pin-icon {
  width: 20px;
  height: 20px;
  background: var(--color-surface-reverse);
  border: 2px solid var(--color-border);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  margin-top: -6px;
  transition: all 0.3s ease;
}

.progress-pin.achieved .pin-icon {
  background: var(--color-brand);
  border-color: var(--color-brand);
  transform: scale(1.1);
}

.progress-pin .pin-label {
  font-size: 10px;
  color: var(--color-text-tertiary);
  margin-top: 2px;
  white-space: nowrap;
}

.progress-pin.achieved .pin-label {
  color: var(--color-text-primary);
  font-weight: 600;
}

/* 보상 배지 */
.reward-badges {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}

.reward-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 10px 4px;
  background: var(--color-surface);
  border-radius: 10px;
  border: 2px solid var(--color-border);
  transition: all 0.3s ease;
  opacity: 0.5;
}

.reward-badge.achieved {
  opacity: 1;
  border-color: var(--color-brand);
  background: var(--color-surface-reverse);
}

.reward-badge.gift.achieved {
  border-color: #4ade80;
}

.reward-badge.overtime.achieved {
  border-color: #60a5fa;
}

.reward-badge .badge-icon {
  font-size: 20px;
}

.reward-badge .badge-text {
  font-size: 10px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.reward-badge .badge-status {
  font-size: 9px;
  color: var(--color-text-tertiary);
}

.reward-badge.achieved .badge-status {
  color: var(--color-brand);
  font-weight: 700;
}

.reward-badge.gift.achieved .badge-status {
  color: #22c55e;
}

.reward-badge.overtime.achieved .badge-status {
  color: #3b82f6;
}

.reward-badge.ticket.achieved {
  border-color: #f59e0b;
  background: linear-gradient(135deg, var(--color-surface-reverse), rgba(245, 158, 11, 0.08));
}

.reward-badge.ticket.achieved .badge-status {
  color: #f59e0b;
}

/* 룰렛 티켓 지급 알림 */
.ticket-reward-banner {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 2px solid #f59e0b;
  border-radius: 12px;
  animation: ticketPulse 1.5s ease-in-out infinite;
}

.ticket-reward-banner .ticket-icon {
  font-size: 20px;
  animation: ticketBounce 0.6s ease-in-out;
}

.ticket-reward-banner .ticket-text {
  font-size: 14px;
  color: #92400e;
  font-weight: 500;
}

.ticket-reward-banner .ticket-text strong {
  color: #b45309;
  font-weight: 800;
}

@keyframes ticketPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.3); }
  50% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
}

@keyframes ticketBounce {
  0% { transform: scale(0.5) rotate(-15deg); opacity: 0; }
  50% { transform: scale(1.3) rotate(5deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
}

/* 오늘의 메시지 */
.today-message {
  text-align: center;
  padding: 0 8px;
}

.message-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0;
  line-height: 1.5;
}

/* 닫기 버튼 */
.close-btn {
  width: 100%;
  padding: 14px;
  background: var(--color-brand);
  color: var(--color-text-black);
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(var(--color-brand-rgb, 255, 200, 0), 0.4);
}

.close-btn:active {
  transform: translateY(0);
}

/* Confetti */
.confetti-container {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  background: var(--color);
  top: -10px;
  left: var(--x);
  animation: confettiFall var(--duration) ease-out var(--delay) forwards;
  border-radius: 2px;
}

.confetti:nth-child(odd) {
  border-radius: 50%;
}

@keyframes confettiFall {
  0% {
    transform: translateY(0) rotate(0deg) scale(1);
    opacity: 1;
  }
  100% {
    transform: translateY(400px) rotate(720deg) scale(0.5);
    opacity: 0;
  }
}

/* 마일스톤 버스트 */
.milestone-burst {
  position: absolute;
  inset: 0;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.burst-ring {
  position: absolute;
  width: 100px;
  height: 100px;
  border: 3px solid var(--color-brand);
  border-radius: 50%;
  animation: burstExpand 1s ease-out forwards;
}

.burst-ring.delay-1 { animation-delay: 0.2s; }
.burst-ring.delay-2 { animation-delay: 0.4s; }

@keyframes burstExpand {
  0% { transform: scale(0); opacity: 1; }
  100% { transform: scale(4); opacity: 0; }
}

/* Step 전환 트랜지션 */
.slide-fade-enter-active {
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateX(30px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}

/* 모달 트랜지션 */
.modal-enter-active {
  animation: fadeIn 0.3s ease-out;
}
.modal-enter-active .attendance-popup {
  animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.modal-leave-active {
  animation: fadeOut 0.2s ease-in forwards;
}
.modal-leave-active .attendance-popup {
  animation: scaleOut 0.2s ease-in forwards;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

@keyframes scaleIn {
  from { 
    opacity: 0;
    transform: scale(0.9) translateY(20px); 
  }
  to { 
    opacity: 1;
    transform: scale(1) translateY(0); 
  }
}

@keyframes scaleOut {
  from { 
    opacity: 1;
    transform: scale(1) translateY(0); 
  }
  to { 
    opacity: 0;
    transform: scale(0.95) translateY(10px); 
  }
}

@media print {
  .attendance-overlay {
    display:none !important;
  }
}
</style>